import { useMemo } from 'react';
import { chain, keyBy } from 'lodash';
import type { MenuProps } from '@timeedit/ui-components/antd';
import { Card, CardBody, CardFooter, CardItem, type CardProps } from '..';
import type { IOption } from './option.type';

export type DropdownMenuProps = Omit<CardProps, 'onChange'> & {
  title?: string;
  tooltip?: string;
  options: IOption[];
  selectedKeys?: string[];
  readonly?: boolean;
  footer?: React.ReactNode;
  onChange?: (selectedKeys: string[]) => void;
};

const DropdownMenuCard = ({
  title,
  tooltip,
  options,
  selectedKeys = [],
  readonly,
  footer,
  onChange,
  ...rest
}: DropdownMenuProps) => {
  const optionMap: Record<string, IOption> = useMemo(
    () => keyBy(options, 'key'),
    [options]
  );

  const menu: MenuProps = useMemo(
    () => ({
      items: chain(options)
        .filter((option) => !selectedKeys.includes(option.key))
        .value(),
      selectedKeys,
      onClick: ({ key }) => {
        onChange?.([...selectedKeys, key]);
      },
    }),
    [options, selectedKeys, onChange]
  );

  return (
    <Card
      title={title}
      tooltip={tooltip}
      footer={footer || (!readonly && <CardFooter.DropdownMenu menu={menu} />)}
      {...rest}
    >
      <CardBody.VirtualList data={selectedKeys}>
        {(key) => {
          const item = optionMap[key];
          if (!item) return <div />;
          return (
            <CardItem.TextItem
              title={item.label}
              url={item.url}
              onDelete={
                !item.disable && !!onChange
                  ? () =>
                      onChange(selectedKeys.filter((curKey) => key !== curKey))
                  : null
              }
              data-testid={
                rest['data-testid'] && `${rest['data-testid']}-${key}`
              }
            />
          );
        }}
      </CardBody.VirtualList>
    </Card>
  );
};

export default DropdownMenuCard;
